import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import apiClient from '../api/client';
import type { TrainingResource } from '../types';
import { fetchAllTraining, createTraining, deleteTraining } from '../api/training';
import { fetchSkillNames } from '../api/skillDevelopment';

interface PurgeResult {
  primary_count: number;
  secondary_count: number;
  skills_count: number;
  history_count: number;
  goals_count: number;
}

export default function Admin() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('admin.title', 'Administration')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('admin.subtitle', 'System administration and data management.')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PurgeCard
          title={t('admin.purgeCvs', 'Purge CV Data')}
          description={t('admin.purgeCvsDesc', 'Delete all CVs, embeddings, extracted skills, skill history, and development goals. Employee records are preserved.')}
          endpoint="/admin/purge-cvs"
          confirmText={t('admin.confirmPurgeCvs', 'Type DELETE to confirm CV data purge')}
          colorClass="border-red-200"
        />
        <PurgeCard
          title={t('admin.purgeTenders', 'Purge Tender Data')}
          description={t('admin.purgeTendersDesc', 'Delete all tenders and match results. Employee and CV data are preserved.')}
          endpoint="/admin/purge-tenders"
          confirmText={t('admin.confirmPurgeTenders', 'Type DELETE to confirm tender data purge')}
          colorClass="border-red-200"
        />
      </div>

      <TrainingManager />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Training Resource Manager
// ---------------------------------------------------------------------------

function TrainingManager() {
  const { t } = useTranslation();
  const [resources, setResources] = useState<TrainingResource[]>([]);
  const [skillNames, setSkillNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    skillName: '', title: '', provider: 'Udemy Business', url: '',
    resourceType: 'COURSE', estimatedHours: '', description: '',
  });
  const [saving, setSaving] = useState(false);

  const loadResources = useCallback(() => {
    setLoading(true);
    fetchAllTraining()
      .then(setResources)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadResources(); }, [loadResources]);

  useEffect(() => {
    fetchSkillNames().then(setSkillNames).catch(() => {});
  }, []);

  const skillOptions = useMemo(() =>
    skillNames.map((s) => ({ value: s, label: s })),
    [skillNames],
  );

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.skillName.trim() || !form.title.trim()) return;
    setSaving(true);
    try {
      await createTraining({
        skill_name: form.skillName.trim(),
        title: form.title.trim(),
        provider: form.provider,
        url: form.url || null,
        resource_type: form.resourceType,
        estimated_hours: form.estimatedHours ? parseFloat(form.estimatedHours) : null,
        description: form.description || null,
      });
      setShowAdd(false);
      setForm({ skillName: '', title: '', provider: 'Udemy Business', url: '', resourceType: 'COURSE', estimatedHours: '', description: '' });
      loadResources();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTraining(id);
      loadResources();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t('admin.trainingTitle', 'Training Resources')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('admin.trainingDesc', 'Manage training courses, certifications, and learning paths linked to skills.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
        >
          {showAdd ? t('common.cancel', 'Cancel') : t('admin.addTraining', '+ Add Resource')}
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">{error}</div>
      )}

      {showAdd && (
        <form onSubmit={handleAdd} className="rounded-lg border border-brand-200 bg-brand-50 p-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingSkill', 'Skill Name')}</label>
              <Select
                options={skillOptions}
                value={skillOptions.find((o) => o.value === form.skillName) ?? null}
                onChange={(opt) => setForm({ ...form, skillName: opt?.value ?? '' })}
                placeholder={t('admin.selectSkill', 'Select a skill...')}
                isClearable
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                  option: (base) => ({ ...base, fontSize: '0.875rem' }),
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingCourseTitle', 'Course Title')}</label>
              <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="block w-full rounded-md border-gray-300 text-sm" placeholder="e.g. CKA Exam Prep" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingProvider', 'Provider')}</label>
              <input type="text" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="block w-full rounded-md border-gray-300 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingUrl', 'URL')}</label>
              <input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="block w-full rounded-md border-gray-300 text-sm" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingType', 'Type')}</label>
              <Select
                options={[
                  { value: 'COURSE', label: 'Course' },
                  { value: 'CERTIFICATION', label: 'Certification' },
                  { value: 'WORKSHOP', label: 'Workshop' },
                  { value: 'BOOK', label: 'Book' },
                  { value: 'VIDEO', label: 'Video' },
                  { value: 'PATH', label: 'Learning Path' },
                ]}
                value={{ value: form.resourceType, label: form.resourceType === 'PATH' ? 'Learning Path' : form.resourceType.charAt(0) + form.resourceType.slice(1).toLowerCase() }}
                onChange={(opt) => setForm({ ...form, resourceType: opt?.value ?? 'COURSE' })}
                classNamePrefix="react-select"
                isSearchable={false}
                styles={{
                  control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                  option: (base) => ({ ...base, fontSize: '0.875rem' }),
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingHours', 'Est. Hours')}</label>
              <input type="number" step="0.5" min="0" value={form.estimatedHours} onChange={(e) => setForm({ ...form, estimatedHours: e.target.value })}
                className="block w-full rounded-md border-gray-300 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('admin.trainingDescription', 'Description')}</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="block w-full rounded-md border-gray-300 text-sm" placeholder={t('admin.trainingDescPlaceholder', 'Optional description')} />
          </div>
          <button type="submit" disabled={saving}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? t('common.saving', 'Saving...') : t('admin.saveTraining', 'Save Resource')}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8" role="status">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
        </div>
      ) : resources.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
          {t('admin.noTraining', 'No training resources yet. Click "+ Add Resource" to create one.')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('admin.trainingSkill', 'Skill')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('admin.trainingCourseTitle', 'Title')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('admin.trainingProvider', 'Provider')}</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">{t('admin.trainingType', 'Type')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('admin.trainingHours', 'Hours')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('common.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {resources.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">{r.skill_name}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{r.title}</a> : r.title}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{r.provider}</td>
                  <td className="px-4 py-3 text-center"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{r.resource_type}</span></td>
                  <td className="px-4 py-3 text-right text-gray-500">{r.estimated_hours ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => handleDelete(r.id)} className="text-xs text-red-500 hover:text-red-700">
                      {t('common.delete', 'Delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Purge Card
// ---------------------------------------------------------------------------

function PurgeCard({ title, description, endpoint, confirmText, colorClass }: {
  title: string;
  description: string;
  endpoint: string;
  confirmText: string;
  colorClass: string;
}) {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PurgeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePurge = async () => {
    if (confirmInput !== 'DELETE') return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await apiClient.post<PurgeResult>(endpoint);
      setResult(data);
      setShowConfirm(false);
      setConfirmInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purge failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={['rounded-xl border-2 bg-white p-6 space-y-4', colorClass].join(' ')}>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>

      {result && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700">
          {t('admin.purgeComplete', 'Purge complete.')} {t('admin.recordsDeleted', 'Primary: {{primary}}, Secondary: {{secondary}}', {
            primary: result.primary_count,
            secondary: result.secondary_count,
          })}
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {!showConfirm ? (
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          {t('admin.purge', 'Purge')}
        </button>
      ) : (
        <div className="space-y-3 rounded-lg border border-red-300 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">{confirmText}</p>
          <input
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder="DELETE"
            className="block w-full rounded-md border-red-300 text-sm focus:border-red-500 focus:ring-red-500"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePurge}
              disabled={confirmInput !== 'DELETE' || loading}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? t('admin.purging', 'Purging...') : t('admin.confirmPurge', 'Confirm Purge')}
            </button>
            <button
              type="button"
              onClick={() => { setShowConfirm(false); setConfirmInput(''); }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
